import { useEffect, useState } from 'react';
import MetricsFrameworkApiService from '../apiService/metricsFrameworkApiService';
import { useMetricsFrameworkContext } from '../context/MetricsFrameworkContext';
import type { Filters, MetricDataLevel, MetricResult } from '../types';
import { buildMetricCacheKey } from '../utils/metricCacheKey';
import { metricTileCache, metricTileInflight } from '../utils/tileCache';
import { getTileFetchStatusCode } from '../utils/tileFetchError';

const loadMetric = async (
  id: string,
  filters: Filters,
  mockApiFlag: boolean,
  dataLevel: MetricDataLevel,
  key: string,
  bypassCache: boolean,
) => {
  if (!bypassCache) {
    const cached = metricTileCache.get(key);
    if (cached) return cached;
  }

  const inflight = metricTileInflight.get(key);
  if (inflight) return inflight;

  const request = MetricsFrameworkApiService.getMetric(id, filters, mockApiFlag, dataLevel)
    .then(result => {
      if (!bypassCache) {
        metricTileCache.set(key, result);
      }
      return result;
    })
    .finally(() => {
      metricTileInflight.delete(key);
    });

  metricTileInflight.set(key, request);
  return request;
};

export const useMetric = (
  id: string,
  filters: Filters | null,
  dataLevel: MetricDataLevel = 'L1',
) => {
  const { mockApiFlag, dataRevision } = useMetricsFrameworkContext();
  const [data, setData] = useState<MetricResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusCode, setStatusCode] = useState<number | null>(null);

  useEffect(() => {
    if (!filters?.dealerId || !id) {
      setData(null);
      setIsLoading(false);
      setError('');
      setStatusCode(null);
      return;
    }

    const key = buildMetricCacheKey(id, filters, mockApiFlag, dataRevision, dataLevel);
    // L2/L3 always hit the network so drilldown requests are visible and fresh.
    const bypassCache = dataLevel !== 'L1';
    if (!bypassCache) {
      const cached = metricTileCache.get(key);
      if (cached) {
        setData(cached.data);
        setIsLoading(false);
        setError('');
        setStatusCode(cached.statusCode);
        return;
      }
    }

    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setError('');
      setStatusCode(null);
      try {
        const result = await loadMetric(id, filters, mockApiFlag, dataLevel, key, bypassCache);
        if (!cancelled) {
          setData(result.data);
          setStatusCode(result.statusCode);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load metric';
          setError(message);
          setStatusCode(getTileFetchStatusCode(err) ?? null);
          setData(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [dataLevel, dataRevision, filters, id, mockApiFlag]);

  return { data, isLoading, error, statusCode };
};
